import { useEffect, useState } from "react";
import DizeeInput from "../../../components/DizeeComponent/DizeeInput";
import { verifyEmailOTP, sendOtpToEmailThunk } from "../../../store/auth/authThunk";
import { useDispatch, useSelector } from "react-redux";
import { clearAlerts } from "../../../store/alert/alertSlice";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import { NextButton } from "../../../components/DizeeComponent/NextButton";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: white;
  cursor: pointer;
  height: 18px;
  line-height: 18px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const ResendSection = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
`;

const ResendButton = styled.button`
  font-size: 12px;
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.5)' : '#ffffff'};
  background: none;
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-decoration: none;
  padding: 0;
  
  &:hover {
    color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.5)' : '#ff0000'};
  }
`;

const Step1 = ({ setStep, setUser, user }) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  useEffect(() => {
    // Timer countdown
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!otp) return;
    dispatch(clearAlerts());
    try {
      const result = await dispatch(verifyEmailOTP({ 
        email: user.email,
        code: otp
      })).unwrap();
      if (result.status === 200) {
        setStep(2);
      }
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    
    dispatch(clearAlerts());
    try {
      // Make sure we have the email address
      if (!user?.email) {
        console.error('Email address is required');
        return;
      }
      
      await dispatch(sendOtpToEmailThunk(user.email));
      setResendTimer(30); // Start 30 second cooldown
      setOtp(""); // Clear the input field
    } catch (error) {
      console.error('Resend failed:', error);
    }
  };

  return (
    <Container>
      <FormSection>
        <TypeWriter text={`Enter the code sent to ${user?.email}`} speed={30} />
        <form onSubmit={verifyCode}>
          <DizeeInput
            placeholder={"6-digit code"}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={6}
          />
          <ButtonContainer show={otp.length === 6}>
            <NextButton
              disabled={otp.length !== 6}
              type="submit"
            >
              Verify code
            </NextButton>
          </ButtonContainer>
        </form>
        
        <ResendSection>
          <ResendButton 
            disabled={resendTimer > 0}
            onClick={handleResend}
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
          </ResendButton>
        </ResendSection>
      </FormSection>
      
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          Check your email inbox and spam folder for the verification code.
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step1;
